/**
 * Cross-subdomain SSO return-URL helpers.
 *
 * A subdomain app (e.g. `dna.atlaspd.com`) sends unauthenticated users to
 * `https://atlaspd.com/login?return=<full subdomain URL>`. After a successful
 * Atlas login (including the 2FA detours) we send the browser back to that URL —
 * but only after validating it, so a crafted `?return=` cannot turn the login
 * page into an open redirect.
 */

/** Parent SSO domain. Configurable per-environment; defaults to production. */
const PARENT_DOMAIN = import.meta.env.VITE_SSO_PARENT_DOMAIN || 'atlaspd.com';

/** sessionStorage key used to carry the return URL across the 2FA detour. */
export const SSO_RETURN_STORAGE_KEY = 'atlas_sso_return';

/**
 * Reads the raw return URL from the current query string. Accepts `return`
 * (canonical) or `redirect` (alias). Returns the unvalidated string or null.
 */
export function getReturnUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('return') ?? params.get('redirect');
}

/**
 * Open-redirect guard. A URL is allowed only when it is `https:` AND its host is
 * the parent domain itself or a subdomain of it. Anything else — other schemes,
 * other hosts, or strings that don't parse as URLs — is rejected.
 */
export function isAllowedReturnUrl(raw: string): boolean {
  try {
    const url = new URL(raw);
    if (url.protocol !== 'https:') return false;
    const host = url.hostname.toLowerCase();
    const parent = PARENT_DOMAIN.toLowerCase();
    return host === parent || host.endsWith(`.${parent}`);
  } catch {
    // Not a parseable absolute URL → never trust it.
    return false;
  }
}

/**
 * Returns the validated return URL from the current query string, or null when
 * absent / unsafe. Callers should redirect here only when this is non-null.
 */
export function safeReturnUrl(): string | null {
  const raw = getReturnUrl();
  if (raw && isAllowedReturnUrl(raw)) return raw;
  return null;
}

/**
 * Reads and clears the return URL stashed before a 2FA detour. Re-validates with
 * the same open-redirect guard — storage is never trusted blindly. Returns the
 * safe URL (and clears the key) or null.
 */
export function consumeStoredReturnUrl(): string | null {
  const raw = sessionStorage.getItem(SSO_RETURN_STORAGE_KEY);
  if (!raw) return null;
  sessionStorage.removeItem(SSO_RETURN_STORAGE_KEY);
  return isAllowedReturnUrl(raw) ? raw : null;
}
